<template>
  <div class="page">
    <div class="topSection">
      <h2>{{ $route.meta.title }}</h2>
      <v-btn
        v-if="apiEnabled"
        :disabled="!canChangeApiSettings"
        color="primary"
        @click="generateApiToken"
      >
        <v-icon class="mr-2"> mdi-plus </v-icon>
        new API token
      </v-btn>
    </div>

    <no-module-access-message :module-enabled="apiEnabled" />
    <div v-if="apiEnabled" class="pageLayout">
      <div class="gap tokenSection">
        <div class="textStyling">
          <p class="tokenSize">
            Generating a new API token will invalidate the previous API token.
          </p>
          <p class="documentationText mb-0">
            The documentation provides more details about the API.
          </p>
        </div>
        <div class="gap">
          <h4 v-if="token">API-token</h4>
          <div class="itemRow">
            <div v-if="token">
              <tooltip-copy title="API-token" :data-copy="token" />
            </div>
            <div v-else class="emptyToken" />

            <a :href="documentation.API_DOCS" target="_blank">
              <v-btn class="primary">API documentation</v-btn>
            </a>
          </div>
        </div>

        <div class="itemContainers">
          <h4>Tenant</h4>
          <div class="itemRow">
            <tooltip-copy :data-copy="userStore.environment.id" />
            <a :href="collectionUrl" download>
              <v-btn text>
                <v-icon class="mr-2">mdi-download</v-icon>Collection JSON
              </v-btn>
            </a>
          </div>
          <h4>Base url</h4>
          <div class="itemRow">
            <tooltip-copy :data-copy="baseUrl" />
            <a :href="apiYamlUrl" download>
              <v-btn text>
                <v-icon class="mr-2">mdi-download</v-icon>Open API YAML
              </v-btn></a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useUserStore } from '@/stores/UserStore'
import { MODULES } from '@/constants/modules'
import { PERMISSIONS } from '@/constants/permissions'
import api from '@/api'
import NoModuleAccessMessage from '../components/NoModuleAccessMessage.vue'
import tooltipCopy from '../components/TooltipCopy.vue'
import { DOCUMENTATION } from '@/constants/documentation'

export default {
  name: 'ApiSettings',
  components: { NoModuleAccessMessage, tooltipCopy },

  data() {
    return {
      showCopiedTooltip: false,
      showCopiedTooltipTenant: false,
      showCopiedTooltipUrl: false,
      copiedTooltipTimeout: null,
      copiedTooltipTenantTimeout: null,
      copiedTooltipUrlTimeout: null,
      token: null,
      collectionUrl: '/api-docs/collection.json',
      apiYamlUrl: '/api-docs/openapi.yaml',
    }
  },

  setup() {
    return {
      userStore: useUserStore(),
    }
  },

  computed: {
    baseUrl() {
      return process.env.VUE_APP_API_GLOBAL_URL
    },

    apiEnabled() {
      return this.userStore.hasEnabledModule(MODULES.API)
    },

    canChangeApiSettings() {
      return this.userStore.can(PERMISSIONS.API_SETTINGS)
    },

    documentation() {
      return DOCUMENTATION
    },
  },

  mounted() {
    this.fetchToken()
  },

  methods: {
    async fetchToken() {
      this.token = (await api.get('api-token')).data.token
    },

    copyToken(token) {
      try {
        navigator.clipboard.writeText(token)
        this.showCopiedTooltip = true

        clearTimeout(this.copiedTooltipTimeout)
        this.copiedTooltipTimeout = setTimeout(
          () => (this.showCopiedTooltip = false),
          1000
        )
      } catch (e) {
        this.showCopiedTooltip = false
      }
    },

    copyTenant(token) {
      try {
        navigator.clipboard.writeText(token)
        this.showCopiedTooltipTenant = true

        clearTimeout(this.copiedTooltipTenantTimeout)
        this.copiedTooltipTimeout = setTimeout(
          () => (this.showCopiedTooltipTenant = false),
          1000
        )
      } catch (e) {
        this.showCopiedTooltipTenant = false
      }
    },

    copyUrl(token) {
      try {
        navigator.clipboard.writeText(token)
        this.showCopiedTooltipUrl = true

        clearTimeout(this.copiedTooltipUrlTimeout)
        this.copiedTooltipUrlTimeout = setTimeout(
          () => (this.showCopiedTooltipUrl = false),
          1000
        )
      } catch (e) {
        this.showCopiedTooltipUrl = false
      }
    },

    async generateApiToken() {
      const response = await api.post('api-token')

      this.token = response.data.token
    },
  },
}
</script>

<style scoped>
.token {
  background: #d7e0e3;
  padding: 1rem;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  width: 600px;
}

.emptyToken {
  background: white;
  padding: 1rem;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  width: 600px;
}

.tokenSection {
  grid-column: 1;
  grid-row: 1;
}

.page {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.itemRow {
  display: flex;
  flex-direction: row;
  gap: 4rem;
  align-items: center;
}

.textStyling {
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: space-between;
}

.tokenSize {
  width: 600px;
  wrap-option: no-wrap;
}

.documentationText {
  width: 240px;
}

.pageLayout {
  display: grid;
  grid-template-columns: 2fr 2fr 2fr;
  gap: 4rem;
}

.docs {
  grid-column-start: 2;
  grid-column-end: 3;
  align-items: flex-start;
  display: flex;
  flex-direction: column;
}

.topSection {
  display: flex;
  justify-content: space-between;
  flex-direction: row;
}

.gap {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.itemContainers {
  min-height: 100px;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
  justify-content: center;
}
</style>
